

import useSWR, { mutate } from 'swr';
import { useCallback } from 'react';
import { projectRepository } from '@/features/projects/repository';
import type { ProjectWithZones } from '@/features/projects/types';
import apiClient from '@/shared/api/client';
import { toast } from "sonner";

/**
 * プロジェクト取得フックの返り値
 */
interface UseProjectReturn {
    project: ProjectWithZones | null;
    isLoading: boolean;
    error: Error | null;
    mutate: () => void;
    // CRUD操作
    updateProject: (projectData: Partial<ProjectWithZones>) => Promise<boolean>;
    deleteProject: () => Promise<boolean>;
}

/**
 * 関連するキャッシュキーを取得
 */
const getRelatedCacheKeys = (projectId: string): string[] => {
    return [
        `/projects/${projectId}`,
        '/projects',
        `/projects/${projectId}/daily-activities`,
        // REMOVED: material-usages excluded from GenbaFlow
        `/projects/${projectId}/costs`,
        `/projects/${projectId}/with-zones`, // 工区情報キャッシュも追加
    ];
};

/**
 * キャッシュを一括更新するヘルパー
 */
const updateRelatedCaches = async (projectId: string) => {
    const cacheKeys = getRelatedCacheKeys(projectId);
    await Promise.all(
        cacheKeys.map(pattern =>
            mutate(
                (key) => typeof key === 'string' && key.startsWith(pattern),
                undefined,
                { revalidate: true }
            )
        )
    );
};

/**
 * 単一プロジェクトを取得するフック
 * @param projectId プロジェクトID
 * @returns プロジェクトデータとCRUD操作
 */
export const useProject = (projectId: string | null): UseProjectReturn => {
    const swrKey = projectId ? `/projects/${projectId}` : null;

    const { data, error, isLoading, mutate: swrMutate } = useSWR<ProjectWithZones | null>(
        swrKey,
        async () => {
            if (!projectId) return null;
            try {
                const projectData = await projectRepository.getProject(projectId);
                if (!projectData) {
                    throw new Error('プロジェクトが見つかりません');
                }
                return projectData;
            } catch (error) {
                console.error('Error fetching project with zones:', error);
                throw error;
            }
        },
        {
            revalidateOnFocus: false,
            dedupingInterval: 5000,
        }
    );

    /**
     * プロジェクトを更新する
     * @param projectData 更新するプロジェクトデータ
     * @returns 成功可否
     */
    const updateProject = useCallback(async (projectData: Partial<ProjectWithZones>): Promise<boolean> => {
        if (!projectId) {
            toast.error('プロジェクトIDが指定されていません');
            return false;
        }

        try {
            await apiClient.patch(`/projects/${projectId}`, projectData);

            // 関連するキャッシュを更新
            await updateRelatedCaches(projectId);
            await swrMutate();

            toast.success('プロジェクトを更新しました');
            return true;
        } catch (error: unknown) {
            console.error('Error updating project:', error);
            toast.error(`プロジェクトの更新に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
            return false;
        }
    }, [projectId, swrMutate]);

    /**
     * プロジェクトを削除する
     * @returns 成功可否
     */
    const deleteProject = useCallback(async (): Promise<boolean> => {
        if (!projectId) {
            toast.error('プロジェクトIDが指定されていません');
            return false;
        }

        try {
            await apiClient.delete(`/projects/${projectId}`);

            // 関連するキャッシュを更新
            await updateRelatedCaches(projectId);

            toast.success('プロジェクトを削除しました');
            return true;
        } catch (error: unknown) {
            console.error('Error deleting project:', error);
            toast.error(`プロジェクトの削除に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
            return false;
        }
    }, [projectId]);

    return {
        project: data || null,
        isLoading,
        error,
        mutate: swrMutate,
        updateProject,
        deleteProject,
    };
}; 